import { CSSProperties, FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import axios from 'lib/axios'
import { MainLayout } from 'views/layouts'
import { PrimaryButton as BluesitePrimaryButton, StrokedButton } from 'views/components/UI/Buttons'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material'
import TrainingProcesses from './processes'
import { AcceptanceCopy, downloadAcceptanceCopy } from '../../TrainingRegistration/acceptanceCopy'

const compactButtonStyle: CSSProperties = {
  minWidth: 'auto',
  minHeight: 32,
  padding: '4px 9px',
  fontSize: 12,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
}

interface SeatLink {
  id: number
  status: 'issued' | 'redeemed' | 'revoked'
  training_space_id: number | null
  token: string | null
  registration_id: number | null
  name: string | null
  email: string | null
  job_title: string | null
  phone: string | null
  registration_status: string | null
  confirmation_email_status: string | null
  confirmation_email_sent_at: string | null
  certificate_status: string | null
  requires_reacceptance: boolean
}

interface Inventory {
  id: number
  name: string
  company_name: string
  allocated: number
  consumed: number
  links: SeatLink[]
}

interface SeatActionProps extends SeatLink {
  onDeleteSeat: (link: Pick<SeatLink, 'id' | 'status'>) => Promise<void>
  onRegenerateLink: (linkId: number) => Promise<void>
  onApprove: (registrationId: number) => void
  onDownloadAcceptance: (registrationId: number) => void
  onResendConfirmation: (registrationId: number) => void
  onEditRegistration: (link: Pick<SeatLink, 'registration_id' | 'name' | 'email' | 'job_title' | 'phone'>) => void
  busy: boolean
}

const SeatActions: FC<SeatActionProps> = ({
  id,
  status,
  registration_id,
  certificate_status,
  training_space_id,
  requires_reacceptance,
  confirmation_email_status,
  name,
  email,
  job_title,
  phone,
  onDeleteSeat,
  onRegenerateLink,
  onApprove,
  onDownloadAcceptance,
  onResendConfirmation,
  onEditRegistration,
  busy,
}) => (
  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="nowrap" useFlexGap sx={{ width: 'max-content', minWidth: 'max-content', whiteSpace: 'nowrap' }}>
    {certificate_status !== 'sent' && certificate_status !== 'sending' && (
      <Tooltip title="Eliminar cupo">
        <span>
          <StrokedButton
            size="sm"
            ariaLabel="Eliminar cupo"
            style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }}
            disabled={busy}
            onClick={() => onDeleteSeat({ id, status })}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </StrokedButton>
        </span>
      </Tooltip>
    )}
    {status === 'issued' && (
      <Tooltip title="Regenerar enlace">
        <span>
          <StrokedButton
            size="sm"
            ariaLabel="Regenerar enlace"
            style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }}
            disabled={busy}
            onClick={() => onRegenerateLink(id)}
          >
            <AutorenewIcon sx={{ fontSize: 18 }} />
          </StrokedButton>
        </span>
      </Tooltip>
    )}
    {registration_id && (
      <Tooltip title="Reenviar confirmación de inscripción">
        <span>
          <StrokedButton size="sm" ariaLabel="Reenviar confirmación de inscripción" style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }} disabled={busy || confirmation_email_status === 'sending'} onClick={() => onResendConfirmation(registration_id)}>
            <MailOutlineIcon sx={{ fontSize: 18 }} />
          </StrokedButton>
        </span>
      </Tooltip>
    )}
    {registration_id && certificate_status !== 'sent' && certificate_status !== 'sending' && (
      <Tooltip title="Editar datos de inscripción">
        <span>
          <StrokedButton size="sm" ariaLabel="Editar datos de inscripción" style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }} disabled={busy || confirmation_email_status === 'sending'} onClick={() => onEditRegistration({ registration_id, name, email, job_title, phone })}>
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </StrokedButton>
        </span>
      </Tooltip>
    )}
    {registration_id && training_space_id && (
      <Tooltip title="Descargar documento y constancia de aceptación">
        <span><StrokedButton size="sm" ariaLabel="Descargar documento aceptado" style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }} disabled={busy} onClick={() => onDownloadAcceptance(registration_id)}><DescriptionOutlinedIcon sx={{ fontSize: 18 }} /></StrokedButton></span>
      </Tooltip>
    )}
    {registration_id && (
      <Tooltip title={requires_reacceptance ? 'La persona debe verificar su correo y aceptar el documento vigente' : certificate_status === 'sent' ? 'Certificado enviado' : certificate_status === 'sending' ? 'Envío pendiente de revisión' : certificate_status === 'failed' ? 'Reintentar certificado' : 'Generar certificado'}>
        <span>
          <StrokedButton size="sm" ariaLabel="Generar certificado" style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }} disabled={busy || requires_reacceptance || certificate_status === 'sent' || certificate_status === 'sending'} onClick={() => onApprove(registration_id)}>
            <CardMembershipOutlinedIcon sx={{ fontSize: 18 }} />
          </StrokedButton>
        </span>
      </Tooltip>
    )}
  </Stack>
)

const spreadsheetText = (value: string | null): string => {
  const text = value || ''
  return /^[=+@-]/.test(text) ? `'${text}` : text
}

const personalEmailDomains: Set<string> = new Set(['gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es', 'live.com', 'live.es', 'msn.com', 'yahoo.com', 'yahoo.es', 'icloud.com', 'me.com', 'aol.com', 'proton.me', 'protonmail.com'])

const downloadSeatsExcel = (inventory: Inventory) => {
  const rows = inventory.links.map((link, index) => ({
    Cupo: index + 1,
    Enlace: link.token ? `${window.location.origin}/entrenamientos/${link.token}` : '',
    Nombre: spreadsheetText(link.name),
    Correo: spreadsheetText(link.email),
    Estado: link.status === 'issued'
      ? 'Pendiente de inscripción'
      : link.certificate_status === 'sent'
        ? 'Certificado enviado'
        : link.certificate_status === 'sending' ? 'Envío en revisión' : link.requires_reacceptance ? 'Requiere nueva aceptación' : 'Inscrita',
  }))
  const worksheet = XLSX.utils.json_to_sheet(rows)
  worksheet['!cols'] = [{ wch: 8 }, { wch: 52 }, { wch: 28 }, { wch: 32 }, { wch: 28 }]
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Detalle de cupos')
  XLSX.writeFile(workbook, `detalle-cupos-entrenamiento-${inventory.id}.xlsx`)
}

interface TrainingSeatsProps {
  inventoryId: string
  embedded?: boolean
}

export const TrainingSeats: FC<TrainingSeatsProps> = ({ inventoryId, embedded = false }) => {
  const [inventory, setInventory] = useState<Inventory | null>(null)
  const [allocated, setAllocated] = useState('0')
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [editing, setEditing] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ name: '', email: '', job_title: '', phone: '' })
  const [editError, setEditError] = useState('')

  const load = useCallback(async (id: string, clearError = true) => {
    if (!id) return
    if (clearError) setError('')
    try {
      const response = await axios.get(`/trainings/inventory/${id}`)
      const next = response.data.data as Inventory
      setInventory(next)
      setAllocated(String(next.allocated))
    } catch (requestError: any) {
      setInventory(null)
      setError(requestError.response?.data?.message || 'No se pudo consultar el inventario.')
    }
  }, [])

  useEffect(() => { load(inventoryId) }, [load, inventoryId])

  const saveQuota = async () => {
    if (!inventory) return
    setBusy(true)
    setError('')
    try {
      await axios.put(`/trainings/inventory/${inventory.id}`, { allocated: Number(allocated) })
      await load(String(inventory.id))
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo guardar el cupo.')
    } finally {
      setBusy(false)
    }
  }

  const regenerateLink = async (linkId: number) => {
    if (!inventory) return
    setBusy(true)
    setError('')
    try {
      await axios.post(`/trainings/inventory/${inventory.id}/links/${linkId}/regenerate`)
      await load(String(inventory.id))
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo regenerar el enlace.')
    } finally {
      setBusy(false)
    }
  }

  const deleteSeat = async (link: Pick<SeatLink, 'id' | 'status'>) => {
    if (!inventory) return
    const occupied = link.status === 'redeemed'
    if (occupied && !window.confirm('Este cupo está ocupado. Se anulará y el total disminuirá, pero se conservarán la inscripción y la aceptación legal. ¿Quieres continuar?')) return
    setBusy(true)
    setError('')
    try {
      await axios.delete(`/trainings/inventory/${inventory.id}/links/${link.id}`, { data: { confirm_occupied: occupied } })
      await load(String(inventory.id))
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo eliminar el cupo.')
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async (link: Pick<SeatLink, 'id' | 'token'>) => {
    if (!link.token) return
    const url = `${window.location.origin}/entrenamientos/${link.token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedLinkId(link.id)
    } catch {
      setError('No se pudo copiar el enlace. Selecciónalo y cópialo manualmente.')
    }
  }

  const tableHeaders = [
    { key: 'seat_number', name: 'Cupo' },
    {
      key: 'link',
      name: 'Enlace',
      renderCell: (link: SeatLink) => link.token
        ? <Tooltip title={copiedLinkId === link.id ? 'Enlace copiado' : 'Copiar enlace'}>
            <span>
              <StrokedButton size="sm" ariaLabel="Copiar enlace" style={{ ...compactButtonStyle, width: 34, minWidth: 34, padding: 4 }} onClick={() => copyLink(link)}>
                <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
              </StrokedButton>
            </span>
          </Tooltip>
        : '—',
    },
    { key: 'name', name: 'Nombre' },
    { key: 'email', name: 'Correo' },
    { key: 'display_status', name: 'Estado', renderCell: (link: SeatLink & { display_status: string }) => <>
      <span>{link.display_status}</span>
      {link.registration_id && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
        {link.confirmation_email_status === 'sent'
          ? `Correo enviado${link.confirmation_email_sent_at ? ` · ${dayjs(link.confirmation_email_sent_at).format('DD/MM/YY HH:mm')}` : ''}`
          : link.confirmation_email_status === 'failed'
            ? 'Correo no enviado'
            : link.confirmation_email_status === 'sending'
              ? 'Correo: enviándose'
              : 'Correo pendiente'}
      </Typography>}
    </> },
  ]

  const approve = async (registrationId: number) => {
    setBusy(true)
    setError('')
    try {
      await axios.post(`/trainings/registrations/${registrationId}/approve`)
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo enviar el certificado.')
    } finally {
      await load(String(inventory?.id), false)
      setBusy(false)
    }
  }

  const downloadAcceptance = async (registrationId: number) => {
    setError('')
    try {
      const response = await axios.get(`/trainings/registrations/${registrationId}/acceptances`)
      const acceptances = response.data.data as AcceptanceCopy[]
      if (!acceptances.length) throw new Error('No se encontró la aceptación de esta inscripción.')
      downloadAcceptanceCopy(acceptances[0])
    } catch (requestError: any) { setError(requestError.response?.data?.message || requestError.message || 'No se pudo descargar la aceptación.') }
  }

  const resendConfirmation = async (registrationId: number) => {
    setBusy(true)
    setError('')
    setSuccess('')
    try {
      await axios.post(`/trainings/registrations/${registrationId}/confirmation/retry`)
      setSuccess('SparkPost ha aceptado el reenvío de la confirmación.')
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo reenviar la confirmación.')
    } finally {
      await load(String(inventory?.id), false)
      setBusy(false)
    }
  }

  const openEdit = (link: Pick<SeatLink, 'registration_id' | 'name' | 'email' | 'job_title' | 'phone'>) => {
    if (!link.registration_id) return
    setEditing(link.registration_id)
    setEditForm({ name: link.name || '', email: link.email || '', job_title: link.job_title || '', phone: link.phone || '' })
    setEditError('')
  }

  const saveEdit = async () => {
    if (!editing || !inventory) return
    const details = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      job_title: editForm.job_title.trim(),
      phone: editForm.phone.trim(),
    }
    if (!details.name || !details.email || !details.job_title || !details.phone || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email) || personalEmailDomains.has(details.email.toLowerCase().split('@')[1] || '')) {
      setEditError('Completa los cuatro campos con un correo corporativo válido.')
      return
    }
    setBusy(true)
    setEditError('')
    try {
      await axios.patch(`/trainings/registrations/${editing}`, details)
      await load(String(inventory.id), false)
      setEditing(null)
    } catch (requestError: any) {
      setEditError(requestError.response?.data?.message || 'No se pudieron guardar los datos.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1400 }}>
      {!embedded && <Typography variant="h4" gutterBottom>Cupos de entrenamiento</Typography>}
      {!inventoryId && <Alert severity="info" sx={{ mb: 2 }}>Abre esta pantalla desde el detalle del inventario.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      {inventory && (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Cupos de entrenamiento para inventario: {inventory.name}</Typography>
            <Typography variant="body2" color="text.secondary">{inventory.company_name}</Typography>
            <Typography variant="body2" color="text.secondary">Al guardar, se renuevan los enlaces de los cupos libres. Los cupos inscritos conservan su enlace y sus datos.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr auto 1fr' }, alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>Detalle de cupos</Typography>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'flex-start', lg: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField label="Número total de cupos" type="number" value={allocated} onChange={(event) => setAllocated(event.target.value)} onBlur={() => { if (Number(allocated) < inventory.consumed) setAllocated(String(inventory.consumed)) }} inputProps={{ min: inventory.consumed, step: 1 }} sx={{ width: 220, maxWidth: '100%' }} />
                <BluesitePrimaryButton size="sm" style={compactButtonStyle} background={embedded ? '#397a9d' : undefined} disabled={busy || !Number.isInteger(Number(allocated)) || Number(allocated) < inventory.consumed} onClick={saveQuota}>Guardar cupo</BluesitePrimaryButton>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', lg: 'center' }} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} spacing={1.5} useFlexGap>
                <Button variant="outlined" disabled={inventory.links.length === 0} sx={{ fontSize: 12, minHeight: 32, lineHeight: 1.2, whiteSpace: 'nowrap' }} onClick={() => downloadSeatsExcel(inventory)}>Descargar Excel</Button>
              </Stack>
            </Box>
            <BluesiteTableComponent
              data={inventory.links.map((link, index) => ({
                ...link,
                seat_number: index + 1,
                name: link.name || '—',
                email: link.email || '—',
                display_status: link.status === 'issued'
                  ? 'Pendiente de inscripción'
                  : link.certificate_status === 'sent'
                    ? 'Certificado enviado'
                  : link.certificate_status === 'sending' ? 'Envío en revisión' : link.requires_reacceptance ? 'Requiere nueva aceptación' : 'Inscrita',
              }))}
              headers={tableHeaders}
              ActionComponent={SeatActions}
              extraDataAction={{ onDeleteSeat: deleteSeat, onRegenerateLink: regenerateLink, onApprove: approve, onDownloadAcceptance: downloadAcceptance, onResendConfirmation: resendConfirmation, onEditRegistration: openEdit, busy }}
              loading={false}
              searchBar={false}
              searchLabel=""
              tableStyle="training-seats"
              showNumbers={false}
              minimumColumnWidth={120}
            />
            <Typography color="text.secondary">Cupos asignados: {inventory.allocated} · Inscritas: {inventory.consumed} · Cupos libres: {inventory.allocated - inventory.consumed}</Typography>
          </Stack>
        </Paper>
      )}
      <Dialog open={editing !== null} onClose={() => { if (!busy) setEditing(null) }} fullWidth maxWidth="sm">
        <DialogTitle>Editar datos de inscripción</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          {editError && <Alert severity="error">{editError}</Alert>}
          <TextField label="Nombre completo" value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} required inputProps={{ maxLength: 255 }} />
          <TextField label="Correo electrónico corporativo" type="email" value={editForm.email} onChange={(event) => setEditForm({ ...editForm, email: event.target.value })} required inputProps={{ maxLength: 255 }} />
          <TextField label="Cargo" value={editForm.job_title} onChange={(event) => setEditForm({ ...editForm, job_title: event.target.value })} required inputProps={{ maxLength: 255 }} />
          <TextField label="Teléfono" value={editForm.phone} onChange={(event) => setEditForm({ ...editForm, phone: event.target.value })} required inputProps={{ maxLength: 100 }} />
          <Typography variant="body2" color="text.secondary">Si corriges el nombre o el correo, la persona deberá verificar de nuevo su correo y aceptar el documento actualizado antes de recibir el certificado. Después podrás reenviar la confirmación.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} disabled={busy}>Cancelar</Button>
          <BluesitePrimaryButton size="sm" style={compactButtonStyle} background={embedded ? '#397a9d' : undefined} disabled={busy} onClick={saveEdit}>Guardar cambios</BluesitePrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const Trainings: FC = () => {
  const [searchParams] = useSearchParams()
  const inventoryId = searchParams.get('inventoryId')
  return <MainLayout>{inventoryId ? <TrainingSeats inventoryId={inventoryId} /> : <TrainingProcesses />}</MainLayout>
}

export default Trainings
